import React from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    Divider,
    Collapse,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    ExpandLess, 
    ExpandMore, 
    MenuOpen as MenuOpenIcon,
    Menu as MenuIcon 
} from '@mui/icons-material';
import authService from '../../services/authService';
import menuService from '../../services/menuService';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const Sidebar = ({ userType, onCollapseChange }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = authService.getCurrentUser();
    const userPermissions = currentUser?.role?.permissions || [];
    
    // State for sidebar collapse - persist in localStorage
    const [isCollapsed, setIsCollapsed] = React.useState(() => {
        const saved = localStorage.getItem('sidebarCollapsed');
        return saved ? JSON.parse(saved) : false;
    });
    
    // State for collapsible menu sections
    const [openSections, setOpenSections] = React.useState({
        main: true,
        bookings: true,
        financial: true,
        user_management: true,
        product_management: true,
        management: true,
        reports: true,
        shipments: true,
        profile: true,
        settings: true
    });

    // Get grouped menu items based on user permissions
    const groupedMenuItems = menuService.getGroupedMenuItems(userType, userPermissions);

    // Notify parent component about initial state
    React.useEffect(() => {
        if (onCollapseChange) {
            onCollapseChange(isCollapsed);
        }
    }, [onCollapseChange, isCollapsed]);

    const handleSectionToggle = (section) => {
        if (!isCollapsed) {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
        }
    };

    const handleNavigation = (path) => {
        // Check if user has access to this path before navigating
        if (menuService.hasAccessToPath(path, userType, userPermissions)) {
            navigate(path);
        } else {
            navigate('/unauthorized');
        }
    };

    const toggleSidebar = () => {
        const newCollapsedState = !isCollapsed;
        setIsCollapsed(newCollapsedState);
        
        // Persist state in localStorage
        localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
        
        // Notify parent component about the change
        if (onCollapseChange) {
            onCollapseChange(newCollapsedState);
        }
        
        // Close all sections when collapsing
        if (newCollapsedState) {
            setOpenSections({
                main: false,
                bookings: false,
                financial: false,
                user_management: false,
                product_management: false,
                management: false,
                reports: false,
                shipments: false,
                profile: false,
                settings: false
            });
        } else {
            // Reopen main sections when expanding
            setOpenSections({
                main: true,
                bookings: true,
                financial: true,
                user_management: true,
                product_management: true,
                management: true,
                reports: true,
                shipments: true,
                profile: true,
                settings: true
            });
        }
    };

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
                flexShrink: 0,
                transition: 'width 0.3s ease',
                '& .MuiDrawer-paper': {
                    width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
                    boxSizing: 'border-box',
                    backgroundColor: '#1a237e',
                    color: 'white',
                    transition: 'width 0.3s ease',
                    overflowX: 'hidden',
                    // Custom scrollbar styling - tiny stick
                    '&::-webkit-scrollbar': {
                        width: '1px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '1px',
                        '&:hover': {
                            background: 'rgba(255,255,255,0.5)',
                        },
                    },
                    // Firefox scrollbar styling
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255,255,255,0.3) transparent',
                },
            }}
        >
                        <Box sx={{ 
                p: isCollapsed ? 1 : 2, 
                textAlign: 'center', 
                position: 'relative',
                minHeight: isCollapsed ? 80 : 120, // Maintain consistent height
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}>
                {/* Collapse Toggle Button */}
                <IconButton
                    onClick={toggleSidebar}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.2)',
                        },
                        width: 32,
                        height: 32,
                        zIndex: 1,
                    }}
                >
                    {isCollapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
                </IconButton>

                {!isCollapsed && (
                    <>
                {/* Company Logo */}
                <Box 
                    sx={{ 
                        mb: 1,
                                mt: 2,
                        '& img': {
                            height: '35px',
                            maxWidth: '180px',
                            width: 'auto',
                            objectFit: 'contain',
                            filter: 'brightness(0) invert(1)', // Make logo white for dark sidebar
                        }
                    }}
                >
                    <img 
                        src="/admin/assets/images/logo.png"
                        alt="SunEx Courier"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </Box>
                
                <Typography variant="h6" component="div" sx={{ color: 'white', fontSize: '1rem' }}>
                    Sunex Courier
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {currentUser?.name || 'User'}
                </Typography>
                    </>
                )}
            </Box>
            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.12)' }} />
            
            <List sx={{ pt: 0, flex: 1, overflowY: 'auto' }}>
                {Object.entries(groupedMenuItems).map(([sectionKey, section]) => (
                    <React.Fragment key={sectionKey}>
                        {/* Section Header */}
                        {section.items.length > 0 && sectionKey !== 'main' && !isCollapsed && (
                            <ListItem
                                button
                                onClick={() => handleSectionToggle(sectionKey)}
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.08)',
                                    },
                                    minHeight: 48,
                                }}
                            >
                                <ListItemText 
                                    primary={section.title}
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: 'medium'
                                    }}
                                />
                                {openSections[sectionKey] ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                        )}

                        {/* Menu Items */}
                        <Collapse 
                            in={isCollapsed || sectionKey === 'main' || openSections[sectionKey]} 
                            timeout="auto" 
                            unmountOnExit={!isCollapsed}
                        >
                            {section.items.map((item) => (
                                <Tooltip
                                    key={item.text}
                                    title={isCollapsed ? item.text : ''}
                                    placement="right"
                                    arrow
                                >
                                    <ListItem
                                        button
                                    onClick={() => handleNavigation(item.path)}
                                    sx={{
                                        backgroundColor: location.pathname === item.path 
                                            ? 'rgba(255,255,255,0.12)' 
                                            : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                        },
                                            pl: isCollapsed ? 1.5 : (sectionKey === 'main' ? 2 : 4),
                                            pr: isCollapsed ? 1.5 : 2,
                                            minHeight: 48,
                                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                                    }}
                                >
                                        <ListItemIcon 
                                            sx={{ 
                                                color: 'white', 
                                                minWidth: isCollapsed ? 'auto' : 40,
                                                justifyContent: 'center'
                                            }}
                                        >
                                        {item.icon}
                                    </ListItemIcon>
                                        {!isCollapsed && (
                                    <ListItemText 
                                        primary={item.text}
                                        primaryTypographyProps={{
                                                    fontSize: sectionKey === 'main' ? '0.875rem' : '0.8rem',
                                                    noWrap: true
                                        }}
                                    />
                                        )}
                                </ListItem>
                                </Tooltip>
                            ))}
                        </Collapse>
                    </React.Fragment>
                ))}
            </List>

            {/* User Info at Bottom */}
            {!isCollapsed && (
            <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Role: {currentUser?.role?.name || 'User'}
                </Typography>
                <br />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Permissions: {userPermissions.length}
                </Typography>
            </Box>
            )}

            {isCollapsed && (
                <Box sx={{ 
                    mt: 'auto', 
                    p: 1, 
                    borderTop: '1px solid rgba(255,255,255,0.12)',
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    <Tooltip title={`${currentUser?.role?.name || 'User'} (${userPermissions.length} permissions)`} placement="right" arrow>
                        <Box sx={{ 
                            width: 32, 
                            height: 32, 
                            borderRadius: '50%', 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: 'bold'
                        }}>
                            {(currentUser?.name || 'U').charAt(0).toUpperCase()}
                        </Box>
                    </Tooltip>
                </Box>
            )}
        </Drawer>
    );
};

export default Sidebar; 